import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.heatmap.js';
import Color from 'color';
import Icon from '../../Icon/Icon';
import { useGlobalValue } from '../../../state/GlobalProvider';
import styles from './SalesByWeekDay.module.sass';

const interp = (v1, v2, x) => {
  return v2 * x + v1 * (1 - x);
};

const getGradientColor = (colors, i, base, scaleFactor) => {
  const fIndex = (i - base) * scaleFactor;
  const iIndex = Math.floor(fIndex);
  const iv = fIndex - iIndex;
  let iIndex1 = iIndex + 1;

  if (iIndex1 > colors.length - 1) {
    iIndex1 = iIndex;
  }

  const baseColor = colors[iIndex] && colors[iIndex].length ? new Color(colors[iIndex]).array() : [255, 255, 255];
  const fillColor = colors[iIndex] && colors[iIndex1].length ? new Color(colors[iIndex1]).array() : [255, 255, 255];
  return {
    r: interp(baseColor[0], fillColor[0], iv),
    g: interp(baseColor[1], fillColor[1], iv),
    b: interp(baseColor[2], fillColor[2], iv),
    a: interp(baseColor[3] || 1, fillColor[3] || 1, iv)
  };
};

const monthsNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

const renderChart = (canvas, data, colors, i18n, link, goto) => {
  const style = getComputedStyle(canvas);
  const ctx = canvas.getContext('2d');
  const chartColors = [style.stroke, colors.$themeColor];
  const monthDays = data.map((daysValues, index) => {
    return {
      label: i18n(monthsNames[index]),
      data: daysValues
    };
  });
  const labels = [];

  for (let index = 0; index < 31; index++) {
    labels.push(index + 1);
  }

  canvas.onclick = function (evt) {
    var activeBox = chart.getBoxAtEvent(evt);

    if (link && goto) {
      const filterValue = activeBox.label + "/" + (monthsNames.indexOf(activeBox.datasetLabel) + 1) + "/" + link.to[1];
      goto(link.to, {
        column: link.filterColumn,
        filters: [{
          value: filterValue
        }]
      });
    }
  };

  const chartData = {
    labels: labels,
    datasets: monthDays
  };
  const chart = new Chart(ctx).HeatMap(chartData, {
    responsive: true,
    maintainAspectRatio: false,
    rounded: false,
    colors: chartColors,
    showLabels: false,
    strokeColor: style.borderColor,
    strokePerc: 0.02,
    stroke: true,
    tooltipTemplate: `<%= yLabel %> <%= xLabel %>: <%= value %> docs`,
    scaleFontColor: style.color,
    scaleFontFamily: style.fontFamily
  });
  return chart;
};

const parseLegend = (data, chartColors) => {
  let max = 0;
  let min = Number.MAX_SAFE_INTEGER;
  data.forEach((daysValues, index) => {
    max = Math.max(max, ...daysValues);
    min = Math.min(min, ...daysValues);
    return {
      label: monthsNames[index],
      data: daysValues
    };
  }); // Chart Heat Map Logic

  const dataLength = max - min;
  const legend = [];
  const scaleFactor = (chartColors.length - 1) / (dataLength - 1);
  const base = min;
  const step = (max - min) / 5;

  for (let i = 1; i <= 5; i++) {
    const value = Math.round(min + i * step);
    const color = getGradientColor(chartColors, value, base, scaleFactor);
    legend.push({
      value,
      color
    });
  }

  return legend;
};

const SalesByWeekDay = props => {
  const {
    data: cData,
    title,
    icon,
    link
  } = props || {};
  const {
    theme,
    i18n,
    goto
  } = useGlobalValue();
  const {
    colors
  } = theme || {};
  const canvas = useRef();
  const [style, setStyle] = useState(null);
  useEffect(() => {
    const chart = canvas.current && cData && renderChart(canvas.current, cData, colors, i18n, link, goto);

    if (canvas.current) {
      setStyle(getComputedStyle(canvas.current));
    }

    return () => {
      chart && chart.destroy();
    };
  }, [canvas, cData, colors, i18n]);
  let legendData = [];

  if (cData && canvas && canvas.current && style) {
    const chartColors = [style.borderColor, colors.$themeColor];
    legendData = parseLegend(cData, chartColors).sort(({
      value: a
    }, {
      value: b
    }) => b - a);
  }

  const legend = legendData.map((item, index) => /*#__PURE__*/React.createElement("div", {
    key: index,
    className: styles['legend-item']
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      backgroundColor: `rgba(${item.color.r}, ${item.color.g}, ${item.color.b}, ${item.color.a})`
    }
  }), /*#__PURE__*/React.createElement("small", null, item.value)));
  return /*#__PURE__*/React.createElement("div", {
    className: styles['root']
  }, /*#__PURE__*/React.createElement("h5", {
    className: `widget-title ${styles['title']}`
  }, /*#__PURE__*/React.createElement("div", null, icon && /*#__PURE__*/React.createElement(Icon, {
    className: styles['icon'],
    name: icon
  }), /*#__PURE__*/React.createElement("div", null, title))), /*#__PURE__*/React.createElement("div", {
    className: styles['chart-container']
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['chart']
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("canvas", {
    ref: canvas
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles['legend']
  }, legend && legend.length ? /*#__PURE__*/React.createElement("h6", null, "Docs No") : null, legend)));
};

export default SalesByWeekDay;